import React, { createContext, useContext, useMemo } from 'react';
import { resolveConfig, KonnectAuthAdapter } from './adapters';

/**
 * VibeKonnectContext — every panel below the root can `useVibeKonnect()`
 * to read the resolved config, auth adapter, and computed theme tokens.
 */
const VibeKonnectContext = createContext(null);

export function useVibeKonnect() {
  const ctx = useContext(VibeKonnectContext);
  if (!ctx) {
    throw new Error(
      'useVibeKonnect() must be called inside <VibeKonnect />. ' +
      'If you are embedding into a new host project, wrap your DevConsolePage in <VibeKonnect config={...}>.',
    );
  }
  return ctx;
}

/**
 * <VibeKonnect config={...}>{children}</VibeKonnect>
 *
 * Root component. Provides the pluggable config + auth adapter down the tree
 * via React context. Wraps children (typically <DevConsolePage />) unchanged.
 *
 * Usage inside Konnect:
 *   <VibeKonnect config={DEFAULT_KONNECT_CONFIG}>
 *     <DevConsolePage />
 *   </VibeKonnect>
 *
 * Usage from any other Emergent project:
 *   <VibeKonnect config={{ apiBase, authAdapter: myAuth, theme: 'dark' }}>
 *     <MyDevConsole />
 *   </VibeKonnect>
 *
 * Since the backend enforces its own auth via existing FastAPI Depends,
 * this frontend adapter only needs to know how to attach the JWT to
 * outbound requests — which is exactly what KonnectAuthAdapter provides.
 */
export function VibeKonnect({ config, children }) {
  const resolved = useMemo(() => {
    const merged = resolveConfig(config);
    return {
      ...merged,
      authAdapter: merged.authAdapter || KonnectAuthAdapter,
    };
  }, [config]);

  const value = useMemo(() => ({
    config: resolved,
    authAdapter: resolved.authAdapter,
    branding: resolved.branding,
    apiBase: resolved.apiBase,
    routePrefix: resolved.routePrefix,
    theme: resolved.theme,
  }), [resolved]);

  return (
    <VibeKonnectContext.Provider value={value}>
      {children}
    </VibeKonnectContext.Provider>
  );
}
